/**
 * Helper functions for processing schedule_compliance operational reports
 */

import { formatTime, formatDateTime as formatDateTimeUtil } from '@sophocles/utils/formatUtils';

/**
 * Helper to format seconds to hours and minutes
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
const formatSeconds = seconds => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/**
 * Converts days_of_week array to readable string
 * @param {Array|number} daysOfWeek - Array of day names ["Mon", "Tue"] or bitmask (for backward compatibility)
 * @param {Function} t - Translation function (optional)
 * @returns {string} Formatted days string
 */
export const formatDaysOfWeek = (daysOfWeek, t = null) => {
  if (!daysOfWeek || (Array.isArray(daysOfWeek) && daysOfWeek.length === 0)) return '';

  // Handle array format (new structure)
  if (Array.isArray(daysOfWeek)) {
    // Check if all 7 days are present
    if (daysOfWeek.length === 7) {
      return t ? t('schedule.everyDay', { defaultValue: 'Every day' }) : 'Every day';
    }

    // Translate each day
    const translatedDays = daysOfWeek.map(day => {
      const dayKey = day.toLowerCase();
      return t ? t(`schedule.${dayKey}`, { defaultValue: day }) : day;
    });

    return translatedDays.join(', ');
  }

  // Handle bitmask format (old structure) - for backward compatibility
  if (typeof daysOfWeek === 'number') {
    // 127 = all days (1+2+4+8+16+32+64)
    if (daysOfWeek === 127) {
      return t ? t('schedule.everyDay', { defaultValue: 'Every day' }) : 'Every day';
    }

    const dayNames = t
      ? [
          t('schedule.mon', { defaultValue: 'Mon' }),
          t('schedule.tue', { defaultValue: 'Tue' }),
          t('schedule.wed', { defaultValue: 'Wed' }),
          t('schedule.thu', { defaultValue: 'Thu' }),
          t('schedule.fri', { defaultValue: 'Fri' }),
          t('schedule.sat', { defaultValue: 'Sat' }),
          t('schedule.sun', { defaultValue: 'Sun' }),
        ]
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const days = [];
    for (let i = 0; i < 7; i++) {
      if (daysOfWeek & (1 << i)) {
        days.push(dayNames[i]);
      }
    }

    return days.join(', ');
  }

  return '';
};

/**
 * Extracts load information from schedule compliance report items
 * @param {Object} report - The operational report object
 * @returns {Array} Array of load objects with pass/fail status and details
 */
export const extractLoadDetails = report => {
  if (!report.items || report.items.length === 0) {
    return [];
  }

  // Filter items to only include schedule_compliance items
  const scheduleItems = report.items.filter(
    item => !item.validation_mode || item.validation_mode === 'schedule_compliance',
  );

  return scheduleItems.map(item => {
    const { metadata, status, data } = item;
    const loads = metadata?.loads || [];

    // NEW: Data structure has overall stats and states array
    const hasMultipleStates = metadata?.has_multiple_states || false;
    const states = data?.states || [];

    // Use backend's pre-calculated total runtime (accounts for state validity and overlaps)
    const totalScheduledRuntimeSeconds = metadata?.total_schedule_runtime_seconds;
    const scheduledRuntime =
      totalScheduledRuntimeSeconds != null && totalScheduledRuntimeSeconds > 0
        ? formatSeconds(totalScheduledRuntimeSeconds)
        : null;

    // Aggregate alarms and alarm durations across all states
    let totalAlarmDuration = 0;
    let allAlarms = [];

    states.forEach(state => {
      const alarmDur = state.failing_alarm_duration_seconds;
      if (alarmDur != null && alarmDur > 0) {
        totalAlarmDuration += alarmDur;
      }

      if (state.alarms) {
        allAlarms = allAlarms.concat(state.alarms);
      }
    });

    const alarmDuration = totalAlarmDuration > 0 ? formatSeconds(totalAlarmDuration) : null;

    // Get the most recent state for display (last state in array)
    const currentState = states[states.length - 1] || {};

    // Check if schedule was skipped (configured but time already passed)
    const wasSkipped = currentState.schedule_skipped || false;

    // Get current state's individual duration for display next to times
    const currentStateDuration = currentState.schedule_window_duration_seconds;
    const currentStateRuntime =
      currentStateDuration != null && currentStateDuration > 0 ? formatSeconds(currentStateDuration) : null;

    // Extract schedule config - prefer expected_schedule_config, fall back to schedule_config or state itself
    const scheduleConfig = currentState.expected_schedule_config || currentState.schedule_config || currentState;

    // Also get deployed_schedule_config if it exists (for deployment failures)
    const deployedConfig = currentState.deployed_schedule_config || null;

    return {
      id: item.id,
      loadNames: loads.map(load => load.name).join(', ') || 'Unknown Load',
      loadIds: loads.map(load => load.id),
      status: status, // Overall status (failed if any state failed)
      scheduleId: metadata?.schedule_id || item.subject_key || item.subject_id, // subject_key is new, subject_id for backward compat
      relayNumber: metadata?.relay_number,
      scheduleNumber: metadata?.schedule_number,

      // Display info from current state - use schedule_config or fall back to state fields
      onTime: scheduleConfig.on_time,
      offTime: scheduleConfig.off_time,
      onType: scheduleConfig.on_type,
      offType: scheduleConfig.off_type,
      onOffset: scheduleConfig.on_offset || 0,
      offOffset: scheduleConfig.off_offset || 0,
      alwaysOn: scheduleConfig.always_on || false,
      enabled: scheduleConfig.enabled !== undefined ? scheduleConfig.enabled : true, // Default to true if not specified
      scheduleSkipped: wasSkipped,

      // Info codes - always an array
      infoCodes: currentState.info_codes || item.info_codes || [],

      // Error codes - always an array
      errorCodes: currentState.error_codes || item.error_codes || [],

      failureReason: item.failure_reason || null,

      // Days of week - can be array ["Mon", "Tue"] or bitmask (for backward compatibility)
      daysOfWeek: scheduleConfig.days_of_week || currentState.days_of_week || null,

      // Deployment config mismatch info (when deployment failed but device has different config)
      // Check for deployment-related error codes and exclude DEPLOYMENT_FAILED_BUT_CONFIG_MATCHES
      hasDeploymentMismatch: (() => {
        if (!deployedConfig) return false;

        // Get info codes for checking (always an array)
        const itemInfoCodes = currentState.info_codes || item.info_codes || [];

        // If config matches (DEPLOYMENT_FAILED_BUT_CONFIG_MATCHES), no mismatch
        if (itemInfoCodes.includes('DEPLOYMENT_FAILED_BUT_CONFIG_MATCHES')) {
          return false;
        }

        // Get error codes for checking deployment failures (always an array)
        const itemErrorCodes = currentState.error_codes || item.error_codes || [];

        // Check if there are any deployment-related error codes
        const deploymentErrors = itemErrorCodes.filter(
          code => code && (code.includes('DEPLOYMENT') || code.includes('CONFIG')),
        );

        return deploymentErrors.length > 0;
      })(),
      deployedConfig: deployedConfig
        ? {
            onTime: deployedConfig.on_time,
            offTime: deployedConfig.off_time,
            onType: deployedConfig.on_type,
            offType: deployedConfig.off_type,
            onOffset: deployedConfig.on_offset || 0,
            offOffset: deployedConfig.off_offset || 0,
            alwaysOn: deployedConfig.always_on || false,
            daysOfWeek: deployedConfig.days_of_week || null,
          }
        : null,

      // Current state runtime (for display next to schedule times)
      currentStateRuntime: currentStateRuntime,

      // Aggregated runtime data (uses backend's pre-calculated total)
      scheduledRuntime: scheduledRuntime,
      alarmDuration: alarmDuration,
      estimatedRuntime: scheduledRuntime, // Keep for backwards compatibility

      // All alarms across all states
      alarms: allAlarms,
      alarmCount: data?.overall_alarm_count || allAlarms.length,
      failingAlarmCount: data?.overall_failing_alarm_count || 0,
      informationalAlarmCount: data?.overall_informational_alarm_count || 0,

      // Multi-state tracking
      hasMultipleStates: hasMultipleStates,
      stateCount: states.length,
      states: states, // Include full states array for detailed view
      anyStateFailed: metadata?.any_state_failed || false,
      allStatesPassed: metadata?.all_states_passed || false,

      // Change tracking for tooltip
      changeInfo: hasMultipleStates
        ? states
            .filter(state => {
              // Ignore changes if only 'modified' or 'created' fields were changed
              const changes = state.field_changes || [];
              const meaningfulChanges = changes.filter(
                change => change.field_name !== 'modified' && change.field_name !== 'created',
              );
              return state.changed_by_username && meaningfulChanges.length > 0;
            })
            .map(state => {
              // Filter out metadata-only changes
              const changes = state.field_changes || [];
              const meaningfulChanges = changes.filter(
                change => change.field_name !== 'modified' && change.field_name !== 'created',
              );

              // Map to our internal format
              const fieldChanges = meaningfulChanges.map(change => ({
                field: change.field_name,
                oldValue: change.old_value,
                newValue: change.new_value,
              }));

              return {
                stateNumber: state.state_number,
                changedAt: state.changed_at,
                changedBy: state.changed_by_username,
                fieldChanges: fieldChanges,
              };
            })
        : null,
    };
  });
};

/**
 * Gets a summary of alarms for display
 * @param {Array} alarms - Array of alarm objects
 * @returns {Array} Array of unique alarm summaries
 */
export const getAlarmSummary = alarms => {
  if (!alarms || alarms.length === 0) {
    return [];
  }

  // Each alarm instance is separate - use raised_at to distinguish them
  return alarms.map((alarm, index) => {
    // Format duration if available
    let durationStr = null;
    if (alarm.duration_in_window_seconds != null) {
      const seconds = alarm.duration_in_window_seconds;
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      durationStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    }

    // Backend uses fails_operational_report (true = failing, false = informational)
    const isFailing = alarm.fails_operational_report !== false; // Default to failing if not specified

    return {
      id: `${alarm.alarm_id}_${index}`, // Use index to ensure uniqueness
      alarmId: alarm.alarm_id,
      shortName: alarm.alarm_key,
      displayName: alarm.alarm_display_name || alarm.alarm_key,
      raisedAt: alarm.raised_at,
      clearedAt: alarm.cleared_at,
      isActive: !alarm.cleared_at,
      wasActiveDuringWindow: alarm.was_active_during_window,
      durationInWindow: durationStr,
      durationSeconds: alarm.duration_in_window_seconds,
      relayNumber: alarm.relay_number,
      isInformational: !isFailing,
      isFailing: isFailing,
    };
  });
};

/**
 * Formats schedule time for display using existing relayScheduleUtils
 * @param {string} time - Time string in HH:MM:SS format, HH:MM:SS.microseconds, or ISO datetime
 * @param {string} type - Type of schedule ('time', 'sunset', 'sunrise')
 * @param {number} offset - Offset in minutes (only applies to sunset/sunrise)
 * @param {Object} user - User object for formatting preferences
 * @param {Function} t - Translation function
 * @returns {string} Formatted time string
 */
export const formatScheduleTime = (time, type, offset = 0, user = null, t = null) => {
  if (!time) return '-';

  // Ensure time is a string
  const timeStr = String(time);

  // Clean up time string - remove microseconds and extract from ISO datetime if needed
  let cleanTime = timeStr;

  // Handle ISO datetime format (e.g., "2025-10-21T18:03:42.177924-05:00")
  if (timeStr.includes('T')) {
    cleanTime = timeStr.split('T')[1]?.split('.')[0] || timeStr;
  }
  // Handle time with microseconds (e.g., "18:03:42.177924")
  else if (timeStr.includes('.')) {
    cleanTime = timeStr.split('.')[0];
  }

  // For 'time' type, just format the time without offset
  if (type === 'time' || !type) {
    return formatTime(cleanTime, user);
  }

  // For sunset/sunrise types, show the type with offset if applicable
  if (type === 'sunset' || type === 'sunrise') {
    const typeLabel = t ? t(`sites.${type}`) : type.charAt(0).toUpperCase() + type.slice(1);
    const formattedTime = formatTime(cleanTime, user);

    if (!offset || offset === 0) {
      return `${typeLabel} (${formattedTime})`;
    }

    const offsetLabel = offset > 0 ? `+${offset}m` : `${offset}m`;
    return `${typeLabel} ${offsetLabel} (${formattedTime})`;
  }

  return formatTime(cleanTime, user);
};

/**
 * Gets a summary count for the report
 * @param {Object} report - The operational report object
 * @returns {Object} Summary statistics
 */
export const getScheduleComplianceSummary = report => {
  // Check new structure first (namespaced data), then fall back to old structure
  const hasScheduleCompliance = report.data?.schedule_compliance != null;
  const isLegacyScheduleCompliance = report.validation_mode === 'schedule_compliance';

  if (!hasScheduleCompliance && !isLegacyScheduleCompliance) {
    return null;
  }

  // Use namespaced data if available, otherwise fall back to root data object
  const data = report.data?.schedule_compliance || report.data || {};

  // NEW: Backend now provides summary directly in the new structure
  return {
    totalSchedules: data.total_schedules || 0,
    passedSchedules: data.passed_schedules || 0,
    failedSchedules: data.failed_schedules || 0,
    schedulesWithStateChanges: data.schedules_with_state_changes || 0,
    totalAlarms: data.total_alarms || 0,
    validationPassed: report.status === 'passed',
    timezone: data.timezone || 'UTC',
    lastCheckinTime: data.last_checkin_time || null,
  };
};

/**
 * Formats a datetime string for display using existing format utilities
 * Note: This is a wrapper that re-exports the utility to avoid confusion with the import
 * @param {string} datetime - ISO datetime string
 * @param {Object} user - User object for formatting preferences
 * @returns {string} Formatted datetime
 */
export const formatDateTime = (datetime, user = null) => {
  if (!datetime) return '-';
  return formatDateTimeUtil(datetime, user);
};

/**
 * Checks if a report is a schedule compliance report
 * @param {Object} report - The operational report object
 * @returns {boolean} True if schedule compliance report
 */
export const isScheduleComplianceReport = report => {
  // Check new structure (namespaced data) OR old structure (validation_mode at report level)
  return report?.data?.schedule_compliance != null || report?.validation_mode === 'schedule_compliance';
};

/**
 * Checks if a report is a power compliance report
 * @param {Object} report - The operational report object
 * @returns {boolean} True if power compliance report
 */
export const isPowerComplianceReport = report => {
  return report?.data?.power_compliance != null || report?.validation_mode === 'power_compliance';
};

/**
 * Checks if a report is an image compliance report
 * @param {Object} report - The operational report object
 * @returns {boolean} True if image compliance report
 */
export const isImageComplianceReport = report => {
  return report?.data?.image_compliance != null || report?.validation_mode === 'image_compliance';
};
